/**
 * @fileoverview Main Pinia Store for KeyReply Webchat
 *
 * This file contains the main Pinia store that manages all webchat widget state.
 * Migrated from Vuex to Pinia for Vue 3 compatibility.
 *
 * **Key differences from Vuex:**
 * - No mutations - state changes are made directly in actions
 * - No need for Vue.set() - Vue 3's Proxy-based reactivity handles it
 * - Actions can be async without special handling
 * - Direct `this` access to state and other actions
 * - Better TypeScript support with full type inference
 *
 * **State Categories:**
 * - User & Session: uid, token, user, sessionExpired
 * - Messages: messages, trendingTopics
 * - UI State: chatWindow, displayLauncher, lightbox
 * - Settings: settings object with all configuration
 * - Real-time: socket, socketConnectionStatus
 *
 * @module stores/main
 * @author KeyReply Engineering Team
 */

import { defineStore } from 'pinia'
import { Socket } from 'socket.io-client'
import io from 'socket.io-client'
import Fuse from 'fuse.js'
import { createId as cuid } from '@paralleldrive/cuid2'
import dayjs from 'dayjs'
import advancedFormat from 'dayjs/plugin/advancedFormat'
import * as _ from 'lodash-es'
import Push from 'push.js'
import * as Sentry from '@sentry/vue'
import { AxiosPromise, Method } from 'axios'
import { ElMessageBox } from 'element-plus'

import { initCobrowse } from '@/cobrowse'
import parseJWTPayload from '@/helpers/parseJWTPayload'
import { santizeText, splitQuickReplies } from '@/helpers/sanitizer'
import { getLastActivityDate, NOT_VALID_RESPONSE } from '@/helpers/trendingTopics'
import { FAILED_MESSAGE_ERROR_TEXT, handleWebsocketError } from '@/utils'
import { updateThemeVariables } from '@/styles/theme-variables'
// addUnreadMessageBadge removed - Badge component now uses reactive props
import setDocumentTitle from '@/helpers/setDocumentTitle'
import playNotificationSound from '@/helpers/playNotificationSound'
import pushNotification from '@/helpers/pushNotification'
import initiateFuseInstance from '@/helpers/initiateFuseInstance'
import getParamsFromUrl from '@/helpers/getParamsFromUrl'
import axios, {
  getNewToken,
  isTokenConfigured,
  setTokenToAxiosAuthorizationHeader,
} from '@/helpers/axios'

import type { RootState, Message, Setting, Suggestion } from '../store/types'

dayjs.extend(advancedFormat)

/**
 * Generate Sentry release identifier
 * @returns Release ID in format "name@version"
 */
function releaseId(): string {
  return `${process.env.NAME}@${process.env.VERSION}`
}

/**
 * Validate UUID format (v1-v5)
 * @param uuid - String to validate
 * @returns True if valid UUID format
 */
function isValidUUID(uuid: string): boolean {
  const uuidRegex =
    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[1-5][0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}$/
  return uuidRegex.test(uuid)
}

function decodeJwtPayload(token: string): Record<string, unknown> | null {
  const payload = token.split('.')[1]
  if (!payload) {
    return null
  }

  try {
    const normalized = payload.replace(/-/g, '+').replace(/_/g, '/')
    const decoded = atob(normalized)
    return JSON.parse(decoded)
  } catch (error) {
    return null
  }
}

/**
 * Main Pinia Store
 *
 * Central store for all webchat widget state management.
 * Combines state, getters, and actions in a single store definition.
 */
export const useMainStore = defineStore('main', {
  /**
   * State factory function
   * Returns the initial state for the store
   */
  state: (): RootState => ({
    // User identification - empty until registration
    id: '',
    user: {},

    // JWT notification drawer configuration
    jwtDrawer: {
      enabled: false,
      cancel: false,
      iconType: 'warning',
      title: 'Notification!',
      text: '',
      action: typeof window !== 'undefined' && window.kr_settings?.nativeAction || function () {},
    },

    // Session persistence - restored from localStorage
    uid: typeof localStorage !== 'undefined' ? (localStorage.getItem('_kruid') || '') : '',
    page_id: '',
    token: typeof localStorage !== 'undefined' ? (localStorage.getItem('_krtoken') || '') : '',
    webchatUsersIsAtMaxLimit: false,
    newUser: true,
    simulateUser: false,
    greeted: false,
    server: '',
    sessionExpired: false,
    sessionExpiryTimeout: null,
    webviewUrl: '',
    showWebview: false,
    returnToChatButton: false,
    socketConnectionStatus: false,

    isInitiating: true,
    isSendingPostback: false,

    initialState: {},
    chatWindow: false,
    facebookChatWindow: false,
    online: typeof navigator !== 'undefined' ? navigator.onLine : true,
    lightbox: false,
    messages: [],
    trendingTopics: [],
    currentLanguage: '',

    fuse: undefined,
    socket: null,

    messagePreview: '',
    showMessagePreview: false,
    displayLauncher: false,
    disableTextInput: false,
    settings: {
      trendingTopicBanner: '',
      trendingTopicTitle: '',
      defaultLanguage: '',
      fileUploadStorageType: '',
      session_length: 30,
      title: '',
      font: '',
      resume: false,
      openAutomatically: false,
      showHistoryDownload: true,
      showTextbar: true,
      apps: {},
      subtext: {},
      facebook: {
        page_id: '',
        messenger_app_id: '',
      },
      liveChatStatus: false,
      typingIndicator: {
        status: false,
      },
      agentBubbleColor: '',
      previewBubble: {
        enabled: false,
        message: '',
        delay: 15,
        hide: 30,
        maxLimitReachedMessage:
          "We're experiencing high traffic. Please try again soon. Thank you for your patience!",
        persistPreviewMessageShown: false,
      },
      disableTextInputAfterLivechatSession: false,
      disableUserResolve: false,
      charLimit: { showCountDown: false, limit: 1000 },
      getToken: () => new Promise((resolve) => resolve('token?')),
      chatHistoryMode: 'SESSION',
      stickyMenuConfig: {
        stickyMenuLanguages: [
          {
            lang: 'en',
            value: 'Go to menu',
          },
        ],
      },
      videoVAConfig: {
        subtitleFontSize: 16,
        enableAlphabetButton: true,
      },
      stickyMenu: {},
      stickyMenuLanguage: 'en',
      ui_beta: false,
      rtlMode: false,
      widgetIconColor: '',
      widgetIconTextColor: '',
      headerModalIconColor: '',
      userMainBackgroundColor: '',
      userMainTitleColor: '',
      genai_enabled: false,
      clearAuthorizedUserDataOnClearButton: false,
    },
    allPastMessagesFetched: false,
    failedRequests: [],
    unreadChatsCount: 0,

    isEnabledNotification: false,
    documentTitle: typeof document !== 'undefined' ? document.title : '',
    emailDomainWhitelist: [],
    sessionEnded: false,
    sessionButtonReady: false,

    promptedCobrowseEnded: false,
    liveChatAgentName: '',

    signalRConnectionId: '',
    readMoreConfigure: {
      enabled: false,
      readMoreCharacterCount: 150,
    },
    newChatStyleScroll: false,
  }),

  /**
   * Getters - Computed properties for the store
   * These cache results and only recompute when dependencies change
   */
  getters: {
    /**
     * Get sticky menu text for current language
     * Looks up the menu text based on current stickyMenuLanguage setting
     */
    stickyMenuText: (state): string => {
      let text = 'Go to menu'
      state.settings.stickyMenuConfig.stickyMenuLanguages.forEach((item) => {
        if (item.lang === state.settings.stickyMenuLanguage) text = item.value
      })
      return text
    },

    /**
     * Get sticky menu configuration by language
     */
    stickyMenu: (state) => {
      return state.settings.stickyMenu
    },

    /**
     * Check if UI Beta features are enabled
     */
    isUiBetaEnabled: (state): boolean => {
      return state.settings.ui_beta
    },

    /**
     * Check if Right-to-Left text direction is enabled
     */
    isRtlModeEnabled: (state): boolean => {
      return state.settings.rtlMode
    },

    /**
     * Get current sticky menu language code
     */
    stickyMenuLanguage: (state): string => {
      return state.settings.stickyMenuLanguage
    },
  },

  /**
   * Actions - Methods for async operations and state changes
   * In Pinia, actions replace both Vuex mutations and actions
   */
  actions: {
    // ============================================
    // MUTATION-LIKE ACTIONS (formerly mutations)
    // ============================================

    /**
     * Show JWT error/notification drawer
     */
    jwtMessage({ title, text, iconType = 'warning', cancel = false }: {
      title: string
      text: string
      iconType?: 'warning' | 'info'
      cancel?: boolean
    }) {
      this.jwtDrawer = {
        ...this.jwtDrawer,
        enabled: true,
        title,
        text,
        iconType,
        cancel,
      }
    },

    /**
     * Set text input disabled state
     */
    setDisableTextInput(status: boolean) {
      this.disableTextInput = status
    },

    /**
     * Set trending topics array
     */
    setTrendingTopics(payload: any[]) {
      this.trendingTopics = payload
    },

    /**
     * Set current language code
     */
    setCurrentLanguage(payload: string) {
      this.currentLanguage = payload
    },

    /**
     * Update preview bubble visibility
     */
    updateShowPreviewBubble({ status }: { status: boolean }) {
      this.showMessagePreview = status
    },

    /**
     * Receive multiple messages at once
     */
    receiveAll({ messages }: { messages: Message[] }) {
      messages.forEach((message) => {
        this.messages.push(message)
      })
    },

    /**
     * Set email domain whitelist
     */
    setEmailWhitelist(payload: string[]) {
      this.emailDomainWhitelist = payload
    },

    /**
     * Set custom suggestions for autocomplete
     */
    setCustomSuggestions(payload: Suggestion[]) {
      this.fuse = initiateFuseInstance(payload)
    },

    /**
     * Set webview URL
     */
    setWebviewUrl(url: string) {
      this.webviewUrl = url
    },

    /**
     * Set webview visibility
     */
    setShowWebview(payload: boolean) {
      this.showWebview = payload
    },

    /**
     * Set return to chat button visibility
     */
    setReturnToChat(returnToChatButton: boolean) {
      this.returnToChatButton = returnToChatButton
    },

    /**
     * Receive a single message from the server
     */
    receiveMessage(message: Message) {
      const params = getParamsFromUrl()
      const mobileMode = params.get('mode') === 'mobile'

      if (!_.find(this.messages, { id: message.id })) {
        if (this.settings?.feedbackCollectionEnabled) {
          const [definedQuickReplies, learningQuickReplies] = splitQuickReplies(
            message.data.quickReplies
          )
          message.data.quickReplies = definedQuickReplies
          message.data.learningQuickReplies = learningQuickReplies
        }
        this.messages.push(message)
        if (
          this.settings.enablePushNotification &&
          document.hidden &&
          this.settings.liveChatStatus &&
          !mobileMode
        ) {
          this.unreadChatsCount += 1
          setDocumentTitle(this.documentTitle, this.unreadChatsCount)
          pushNotification(
            'New message',
            message.data.content[0].text,
            this.settings.pushNotificationImageURL,
            () => {
              window.focus()
            }
          )

          const ua = navigator.userAgent
          const isMobileQuery = params.get('mode') === 'mobile'
          const iOS = !!ua.match(/iPad/i) || !!ua.match(/iPhone/i)
          const iOSSDK = !!ua.match(/KeyReplyiOSSDK/i)
          const Android = !!ua.match(/Android/i)
          const Mobile = !!ua.match(/Mobi/i)
          const mobileEnvironment = isMobileQuery || iOS || iOSSDK || Android || Mobile
          if (!mobileEnvironment) {
            playNotificationSound()
          }
        }
        // Badge component now reacts automatically to unreadChatsCount changes
      }
    },

    /**
     * Sync message timestamp with server
     */
    syncMessageTimestamp({ messageIndex, serverTimestamp }: { messageIndex: number; serverTimestamp: Date }) {
      this.messages[messageIndex].timestamp = serverTimestamp
    },

    /**
     * Update socket connection status
     */
    updateSocketConnectionStatus(flag: boolean) {
      this.socketConnectionStatus = flag
    },

    /**
     * Mark initialization as done
     */
    updateInitializingDone() {
      this.isInitiating = false
    },

    /**
     * Add a file message to the messages array
     */
    sendFileMessage({ content, id }: { content: any; id: string }) {
      const last = _.last(_.orderBy(this.messages, ['timestamp'], ['asc']))
      let timestamp = new Date()

      if (last && last.timestamp && new Date(last.timestamp).getTime() > timestamp.getTime()) {
        timestamp = new Date(new Date(last.timestamp).getTime() + 1000)
      }

      const contentType = content.images ? 'images' : content.audio ? 'audio' : 'files'
      const contentObj: any = {}
      contentObj[contentType] = content[contentType]
      const message: Message = {
        id,
        timestamp,
        type: 'message',
        data: {
          content: [contentObj],
        },
      }

      if (!_.find(this.messages, { id })) {
        this.messages.push(message)
      }
    },

    /**
     * Add a text message to the messages array
     */
    addMessage({ type = 'message', text, id, trendingText, timestamp = new Date() }: {
      type?: 'message' | 'postback'
      text: string
      id: string
      trendingText?: string
      timestamp?: Date
    }) {
      const last = _.last(_.orderBy(this.messages, ['timestamp'], ['asc']))

      if (last && last.timestamp && new Date(last.timestamp).getTime() > timestamp.getTime()) {
        timestamp = new Date(new Date(last.timestamp).getTime() + 1000)
      }

      const sanitisedText = santizeText(text)
      const message: Message = {
        id,
        timestamp,
        type,
        data: {
          content: [
            {
              text: sanitisedText,
            },
          ],
        },
        trendingText,
      }

      if (!_.find(this.messages, { id })) {
        this.messages.push(message)
      }
    },

    /**
     * Update message status (sent/failed)
     */
    updateMessageStatusMutation({ id, sent, errorMessage }: { id: string; sent?: boolean; errorMessage?: string }) {
      this.messages = _.map(this.messages, (message) => {
        if (id === message.id) {
          return {
            ...message,
            sent: sent,
            errorMessage,
          }
        }
        return message
      })
    },

    /**
     * Clear all messages
     */
    clearMessages() {
      this.messages = []
    },

    /**
     * Mark all past messages as fetched
     */
    setAllPastMessagesFetched() {
      this.allPastMessagesFetched = true
    },

    /**
     * Set new user flag
     */
    setNewUser(isNewUser = true) {
      this.newUser = isNewUser
    },

    /**
     * Set as existing user
     */
    setExistingUser() {
      this.newUser = false
    },

    /**
     * Set simulate user flag
     */
    setSimulateUser(isSimulatedUser: boolean) {
      this.simulateUser = isSimulatedUser
    },

    /**
     * Set session expired status
     */
    setSessionExpired(status: boolean) {
      this.sessionExpired = status
    },

    /**
     * Set session expiry timeout
     */
    setSessionExpiryTimeout(timer: any) {
      this.sessionExpiryTimeout = timer
    },

    /**
     * Set user ID
     */
    setUserId(id: string) {
      this.uid = id
    },

    /**
     * Set page ID
     */
    setPageId(id: string) {
      this.page_id = id
    },

    /**
     * Set authentication token
     */
    setToken(token: string) {
      this.token = token
    },

    /**
     * Set display launcher visibility
     */
    setDisplayLauncher(status: boolean) {
      this.displayLauncher = status
    },

    /**
     * Set trending topic title color
     */
    setColorTitleTrending(colorTitle: string) {
      this.settings.trendingTopicTitle = colorTitle
    },

    /**
     * Set trending topic banner color
     */
    setColorBannerTrending(colorBanner: string) {
      this.settings.trendingTopicBanner = colorBanner
    },

    /**
     * Merge settings with existing settings
     */
    mergeSettings(settings: Partial<Setting>) {
      if (_.isEmpty(settings)) {
        return
      }

      if (this.settings) {
        const newSettings = Object.assign({}, this.settings, settings)
        this.settings = newSettings
      } else {
        this.settings = settings as Setting
      }

      if (this.settings.botIcon && settings.botIcon) {
        this.settings.botIcon = settings.botIcon
      }

      if (this.settings.suggestions) {
        this.fuse = initiateFuseInstance(this.settings.suggestions)
      }
    },

    /**
     * Update online status from navigator
     */
    updateOnlineStatus() {
      this.online = navigator.onLine
    },

    /**
     * Open chat window
     */
    openChatWindowMutation() {
      window.top?.postMessage('widgetopened', '*')
      this.chatWindow = true
    },

    /**
     * Close chat window
     */
    closeChatWindowMutation() {
      window.top?.postMessage('widgetclosed', '*')
      this.chatWindow = false
    },

    /**
     * Open Facebook chat window
     */
    openFacebookChatWindowMutation() {
      this.facebookChatWindow = true
      ;(window as any).FB.CustomerChat.showDialog()
    },

    /**
     * Close Facebook chat window
     */
    closeFacebookChatWindowMutation() {
      this.facebookChatWindow = false
      ;(window as any).FB.CustomerChat.hideDialog()
    },

    /**
     * Show text bar
     * @param _data - Optional event data (unused, for compatibility)
     */
    showTextbar(_data?: any) {
      localStorage.setItem('showTextbar', 'true')
      this.settings.showTextbar = true
    },

    /**
     * Hide text bar
     */
    hideTextbar() {
      localStorage.removeItem('showTextbar')
      this.settings.showTextbar = false
    },

    /**
     * Toggle live chat banner
     */
    toggleLivechatBanner(status: boolean) {
      if (
        this.settings.liveChatStatus &&
        !status &&
        this.settings.disableTextInputAfterLivechatSession
      ) {
        this.disableTextInput = true
      }
      this.settings.liveChatStatus = status
    },

    /**
     * Set ECE activity ID
     */
    setEceActivityId(eceActivityId: string) {
      this.settings.eceActivityId = eceActivityId
    },

    /**
     * Toggle typing indicator
     */
    toggleTypingIndicatorMutation(status: boolean) {
      this.settings.typingIndicator.status = status
    },

    /**
     * Open dialog/lightbox
     */
    openDialog() {
      this.lightbox = true
    },

    /**
     * Close dialog/lightbox
     */
    closeDialogMutation() {
      this.lightbox = false
    },

    /**
     * Set greeted flag
     */
    setGreeted(greeted: boolean) {
      this.greeted = greeted
    },

    /**
     * Set sending postback flag
     */
    setSendingPostback(isBusy: boolean) {
      this.isSendingPostback = isBusy
    },

    /**
     * Set webchat users at max limit
     */
    setWebchatUsersIsAtMaxLimit(isMax: boolean) {
      this.webchatUsersIsAtMaxLimit = isMax
    },

    /**
     * Clear unread message count
     */
    clearUnreadMessageCount() {
      this.unreadChatsCount = 0
      setDocumentTitle(this.documentTitle)
      // Badge component now reacts automatically to unreadChatsCount changes
    },

    /**
     * Set notification permission status
     */
    setNotificationPermission(isEnabledNotification: boolean) {
      this.isEnabledNotification = isEnabledNotification
    },

    /**
     * Set end session status
     */
    setEndSession(sessionEnded: boolean) {
      this.sessionEnded = sessionEnded
    },

    /**
     * Set session button ready
     */
    setSessionButtonReady() {
      this.sessionButtonReady = true
    },

    /**
     * Set native action callback
     */
    setNativeAction(fn: Function) {
      this.jwtDrawer!.action = fn
    },

    /**
     * Set cobrowse session status
     */
    setCobrowseSession(status: boolean) {
      const cobrowse = (window as any).CobrowseIO
      const enabled = this.settings.cobrowse?.enabled || false
      const key = this.settings.cobrowse?.apiKey || ''
      const promptTitle =
        this.settings.cobrowse?.promptTitle || 'Allow support agent to use this web page with you?'
      const promptMessage = this.settings.cobrowse?.promptMessage || 'Support Request'
      if (cobrowse && !!(enabled && key)) {
        cobrowse.license = key
        cobrowse.customData = {
          user_id: this.uid,
        }
        cobrowse.confirmSession = () => {
          return ElMessageBox.confirm(promptTitle, promptMessage, {
            confirmButtonText: 'Allow',
            cancelButtonText: 'Deny',
            type: 'info',
          })
        }
        if (status) {
          cobrowse.client().then((c: any) => {
            c.start({ allowIFrameStart: true })
          })
        } else {
          cobrowse.client().then((c: any) => {
            c.stop()
          })
        }
      }
    },

    /**
     * Set sticky menu language
     */
    setStickyMenuLanguage(language: string) {
      this.settings.stickyMenuLanguage = language
    },

    /**
     * Set live chat agent name
     */
    setLiveChatAgentName(agentName: string) {
      this.liveChatAgentName = agentName
    },

    /**
     * Set SignalR connection ID
     */
    setSignalRConnection(connection: string) {
      this.signalRConnectionId = connection
    },

    // ============================================
    // ASYNC ACTIONS (formerly Vuex actions)
    // ============================================

    /**
     * START - Main entry point for initializing a chat session
     *
     * This is the primary action called when user opens the chat widget.
     * It orchestrates the entire initialization flow including:
     * 1. Request notification permissions
     * 2. Handle JWT vs anonymous authentication
     * 3. Check/restore previous session based on resume mode
     * 4. Fetch chat history if applicable
     * 5. Establish WebSocket connection
     * 6. Send initial greeting message
     */
    async start() {
      if (this.settings.apps.bot) {
        this.setDisableTextInput(true);
        this.requestNotificationPermission()
        const url = _.get(window, 'kr_settings.getToken.url') as string | undefined
        const method = _.get(window, 'kr_settings.getToken.method') as string | undefined
        if (!this.greeted) {
          this.setGreeted(true)

          if (this.settings.cobrowse?.enabled) {
            await this.fetchCobrowseStatus()
          }

          const ls_krlastseen = localStorage.getItem('_krlastseen')

          const lastSeen = dayjs(ls_krlastseen || undefined)
          const now = dayjs()

          const isSameDay = ls_krlastseen ? now.isSame(lastSeen, 'day') : false

          const hasJWTConfiguration = Boolean(isTokenConfigured(url, method) || this.user.JWT)
          let shouldSendConversationResume = false
          const params = new URLSearchParams(location.search)
          const shouldSendStartMessage = params.get('start') !== '0'
          let isNewSession: boolean = false
          let currentSession: any
          const fetchHistoryByUser = this.settings.chatHistoryMode === 'USER'

          if (hasJWTConfiguration) {
            if (this.user) {
              if (this.settings.resume !== 'NEVER') {
                await this.register()
                currentSession = await this.getCurrentSession()
                const isNewSessionFromResponse = _.get(currentSession, 'data.isNewSession')
                const isContinousSession = _.get(currentSession, 'data.isContinousSession')
                if (!isNewSessionFromResponse || isContinousSession || (isNewSessionFromResponse && fetchHistoryByUser)) {
                  await this.fetchHistory()
                  this.setExistingUser()
                }
              }
            }
          } else if (this.uid) {
            this.setExistingUser()
          }

          switch (this.settings.resume) {
            case 'ALWAYS':
              if (this.uid) {
                if (!hasJWTConfiguration) {
                  await this.fetchHistory()
                }
                if (!isSameDay) {
                  shouldSendConversationResume = true
                }
                break
              } else {
                await this.register()
                await this.login()
              }
              break
            case 'SESSION': {
              if (currentSession === undefined) {
                if (isValidUUID(this.uid)) {
                  currentSession = await this.getCurrentSession()
                  isNewSession = _.get(currentSession, 'data.isNewSession')
                } else isNewSession = true
              }
              if (!isNewSession && ls_krlastseen) {
                if (!this.user || !hasJWTConfiguration) {
                  if (!isSameDay) {
                    shouldSendConversationResume = true
                  }
                  await this.fetchHistory()
                }
              } else if (isNewSession || !hasJWTConfiguration) {
                localStorage.setItem('keyreply-lastOpenStatus', 'false')
                await this.register()
                await this.login()
              }
              break
            }

            case 'NEVER':
              this.toggleLivechatBanner(false)
              await this.register()
              await this.login()
              if (fetchHistoryByUser) {
                this.setExistingUser()
                await this.fetchHistory()
              }
              break
            default:
              await this.login()
          }

          await this.listen()
          await this.sendStartMessage({
            shouldSendConversationResume,
            shouldSendStartMessage,
            hasJWTConfiguration,
            isSameDay,
            isNewSession,
          })
        }
      }
      this.setDisableTextInput(false);
    },

    /**
     * Request browser notification permission
     */
    async requestNotificationPermission() {
      const params = getParamsFromUrl()
      const mobileMode = params.get('mode') === 'mobile'

      if (this.settings.enablePushNotification && !Push.Permission.has() && !mobileMode) {
        ElMessageBox.confirm(
          'Allow Notification Permission to receive message when you change tab',
          'Permission Request',
          {
            confirmButtonText: 'Allow',
            cancelButtonText: 'Deny',
            type: 'info',
            center: true,
          }
        )
          .then(() => {
            Push.Permission.request(
              () => {
                this.setNotificationPermission(true)
              },
              () => {
                this.setNotificationPermission(false)
              }
            )
          })
          .catch(() => {
            this.setNotificationPermission(false)
          })
      } else {
        this.setNotificationPermission(true)
      }
    },

    /**
     * @deprecated Use sendStartMessage instead
     */
    sendStartMessageJwt() {
      let startMessage = null
      if (this.settings.start && this.settings.start.event && this.settings.start.data) {
        startMessage = this.settings.start
      } else {
        startMessage = {
          event: 'goto',
          data: 'conversation_start',
        }
      }

      if (startMessage) {
        return this.sendPostback(startMessage)
      }
    },

    /**
     * Set JWT and resend failed requests
     */
    async setJwtResendRequest({ JWT }: { JWT: string }) {
      setTokenToAxiosAuthorizationHeader(JWT)
      if (this.failedRequests.length > 0) {
        const allRunningRequests: AxiosPromise<any>[] = []
        this.failedRequests.forEach(async (failedRequest) => {
          Object.assign(
            failedRequest,
            { headers: { Authorization: `Bearer ${JWT}` } },
            { data: JSON.parse(failedRequest.data) }
          )
          allRunningRequests.push(axios({ ...failedRequest }))
        })
        await Promise.all(allRunningRequests)
        this.failedRequests = []
      }
    },

    /**
     * Send start message based on session state
     */
    sendStartMessage(
      payload: {
        shouldSendConversationResume?: boolean
        shouldSendStartMessage?: boolean
        hasJWTConfiguration?: boolean
        isSameDay?: boolean
        isNewSession?: boolean
      } = {}
    ) {
      const {
        shouldSendConversationResume,
        shouldSendStartMessage = true,
        hasJWTConfiguration,
        isSameDay,
        isNewSession,
      } = payload
      const params = getParamsFromUrl()
      const tokenProvided = params.get('token')

      let startMessage = null
      if (this.simulateUser || this.newUser || hasJWTConfiguration || tokenProvided) {
        if (this.settings.start && this.settings.start.event && this.settings.start.data) {
          startMessage = this.settings.start

          const customEntryAIA = this.settings.start.event === 'get_agent_achievement_code'
          const inMobileMode = params.get('mode') === 'mobile'
          if (customEntryAIA) {
            startMessage.data =
              (shouldSendStartMessage && !isSameDay) || isNewSession || !inMobileMode ? '1' : '0'
          }
        } else if (shouldSendStartMessage || isNewSession) {
          startMessage = {
            event: 'goto',
            data: 'conversation_start',
          }
        }
      } else if (shouldSendConversationResume) {
        startMessage = {
          event: 'goto',
          data: 'conversation_resume',
        }
      }

      if (startMessage) {
        return this.sendPostback(startMessage)
      }
    },

    /**
     * Establish WebSocket connection
     */
    listen(): Promise<void> {
      return new Promise((resolve, reject) => {
        if (!this.uid || !this.server) {
          reject()
          return
        }

        const baseURL = _.get(window, 'appConfig.BASE_URL', this.server)
        const signalServer = _.get(window, 'appConfig.SIGNAL_SERVER_URL', this.server) || baseURL
        const disablePollingAppConfig = _.get(window, 'appConfig.DISABLE_POLLING')
        const disablePollingKrSettings = _.get(window, 'kr_settings.DISABLE_POLLING')
        const disablePollingDashboardSettings = _.get(this, 'settings.DISABLE_POLLING')
        const disablePolling =
          disablePollingKrSettings || disablePollingAppConfig || disablePollingDashboardSettings
        const socketUri = new URL('/webchat', signalServer).href
        const socketPath = new URL('socket.io', signalServer).pathname

        this.socket = io(socketUri, {
          path: socketPath,
          withCredentials: false,
          transports: disablePolling ? ['websocket'] : ['polling', 'websocket'],
        })

        const socket = this.socket

        let retryCount = 0
        let firstSocketConnect = true
        this.socket.on('disconnect', (reason: string) => {
          this.updateSocketConnectionStatus(false)
          this.toggleTypingIndicator({ flag: false })
          if (reason === 'io server disconnect' && retryCount < 10) {
            socket.connect()
            retryCount++
          }
        })
        this.socket.on('connect', () => {
          socket.emit('uid', { uid: this.uid, page_id: this.page_id || location.hostname })
          retryCount = 0
          if (!firstSocketConnect && this.settings.liveChatStatus) {
            this.fetchHistory({ isInliveChat: true })
            this.setSessionButtonReady()
          } else {
            firstSocketConnect = false
          }
          this.updateSocketConnectionStatus(true)
          this.toggleTypingIndicator({ flag: false })
          this.fetchLivechatStatus()
          resolve()
        })

        this.socket.on('action', async (action: { id: string; payload: any }) => {
          // Dynamically call the action
          const actionName = action.id.toLowerCase().replace(/_([a-z])/g, (g) => g[1].toUpperCase())
          if (typeof (this as any)[actionName] === 'function') {
            await (this as any)[actionName](action.payload)
          }
        })

        this.socket.on('handover_start', (isStarted: boolean) => {
          this.toggleLivechatBanner(isStarted)
        })

        this.socket.on('message', (payload: any) => {
          const messageIndex = this.messages.findIndex((message) => message.id === payload.id)
          const isMessageExist = messageIndex !== -1

          if (this.lightbox) {
            this.closeDialogMutation()
          }

          this.setSendingPostback(false)

          if (!isMessageExist) {
            if (this.chatWindow === false) {
              if (payload.data.content.length === 1) {
                const text = _.get(payload, 'data.content[0].text')
                if (text && typeof text === 'string') {
                  this.messagePreview = text
                  this.showMessagePreview = true
                }
              }
            }

            const autopilot = _.get(payload, 'data.content[0].options.autopilot')
            if (autopilot) {
              if (location.href != autopilot) {
                location.href = autopilot
              }
            }

            this.receiveMessage(payload)
          } else {
            this.syncMessageTimestamp({
              messageIndex,
              serverTimestamp: payload.timestamp,
            })
          }
        })

        this.socket.on('error', (error: Error) => {
          handleWebsocketError(error)
          reject(error)
        })
      })
    },

    /**
     * Fetch message history from server
     */
    async fetchHistory(params?: { isInliveChat?: boolean; fetchMore?: boolean }) {
      const limit = 10
      const offset = params?.isInliveChat ? 0 : this.messages.length
      if (this.uid && this.token) {
        await axios
          .post(new URL('api/webchat_history', this.server).href, {
            page_id: this.page_id || location.hostname,
            user_id: this.uid,
            session_id: localStorage.getItem('_krsessionid'),
            token: this.token,
            limit,
            offset,
          })
          .then((response) => {
            if (response && response.data && !_.isEmpty(response.data.messages)) {
              const messagesToAdd: Message[] = _.clone(response.data.messages)
              const defaultLanguage = this.settings.defaultLanguage || 'ms'
              const currentLanguage = localStorage.getItem('currentLanguage') || defaultLanguage
              localStorage.setItem('currentLanguage', currentLanguage)
              this.setCurrentLanguage(currentLanguage)
              messagesToAdd.forEach((message) => {
                const isMessageExist = this.messages.find((stateMessage) => {
                  const hasSameId = message.id === stateMessage.id
                  return hasSameId
                })

                if (!isMessageExist) {
                  if (this.settings?.feedbackCollectionEnabled) {
                    const quickReplies = _.clone(message.data.quickReplies)
                    const [definedQuickReplies] = splitQuickReplies(quickReplies)
                    message.data.quickReplies = definedQuickReplies
                  }
                  message.timestamp = new Date(message.timestamp)
                  this.messages.unshift(message)
                }
              })
            }

            if (_.get(response, 'data.messages.length', 0) < limit) {
              this.setAllPastMessagesFetched()
            }
            this.setSessionButtonReady()
          })
          .catch(async (error) => {
            await this.register()
            await this.login()
            Sentry.captureException(error)
          })
      }
    },

    /**
     * Register a new user
     */
    async register() {
      return await axios
        .post(new URL('api/webchat_new_user', this.server).href, {
          page_id: location.hostname,
          initial_state: this.settings.initialState,
        })
        .then((response) => {
          if (!response || !response.data) {
            return
          }

          this.setUserId(response.data.uid)
          this.setPageId(response.data.page_id)
          this.setToken(response.data.token)

          Sentry.setTag('UID', response.data.uid)
          Sentry.setUser({ id: response.data.uid })

          if (!this.user.JWT) {
            localStorage.setItem('_kruid', response.data.uid)
            localStorage.setItem('_krtoken', response.data.token)
          }
        })
        .catch((e) => {
          throw e
        })
    },

    /**
     * Get current session from server
     */
    async getCurrentSession() {
      return await axios
        .post(new URL('api/webchat_get_current_session', this.server).href, {
          page_id: this.page_id || location.hostname,
          user_id: this.uid,
          session_length: this.settings.session_length,
          resume_session: this.settings.resume,
        })
        .then((response) => {
          localStorage.setItem('_krsessionid', response.data?.session_id)
          return response
        })
    },

    /**
     * Login / create new session
     */
    async login() {
      if (!this.uid || !this.server) {
        return
      }

      if (this.user) {
        await this.fetchJwt({ user: this.user })
      }

      await axios
        .post(new URL('api/webchat_new_session', this.server).href, {
          page_id: this.page_id || location.hostname,
          user_id: this.uid,
          session_length: this.settings.session_length,
          resume_session: this.settings.resume,
          page_url: location.href,
        })
        .then((response) => {
          this.setNewUser()
          this.setSessionExpired(false)
          this.setSessionButtonReady()
          localStorage.setItem('_krsessionid', response.data.sessionid)
          Sentry.setTag('SESSION_ID', response.data.sessionid)
        })
        .catch((e) => {
          throw e
        })
    },

    /**
     * End current session
     */
    async endSession() {
      const endMessages = {
        event: 'conversation_end_session',
        data: null,
      }
      await this.sendPostback(endMessages)

      if (!_.isEmpty(this.server)) {
        await axios.post(new URL('api/webchat_end_session', this.server).href, {
          page_id: this.page_id || location.hostname,
          user_id: this.uid,
        })
      }
    },

    /**
     * Clear clone state
     */
    async clearCloneState() {
      return await axios.post(new URL('api/webchat_clear_clone_state', this.server).href, {
        page_id: this.page_id || location.hostname,
        user_id: this.uid,
      })
    },

    /**
     * Logout and cleanup
     */
    async logout() {
      if (!this.sessionEnded && !_.isEmpty(this.server)) {
        await axios.post(new URL('api/webchat_end_session', this.server).href, {
          page_id: this.page_id || location.hostname,
          user_id: this.uid,
        })
      }

      this.setGreeted(false)
      this.clearMessages()

      if (this.socket) {
        this.socket.disconnect()
      }

      localStorage.removeItem('showTextbar')
    },

    /**
     * Clear user cache
     */
    async clearCache() {
      this.setUserId('')
      localStorage.removeItem('_kruid')
      localStorage.removeItem('_krtoken')
    },

    /**
     * Clear authorized user data
     */
    async clearAuthorizedUserData() {
      try {
        await axios.post(new URL('api/clear_webchat_cookie', this.server).href, {
          sender: {
            id: this.uid,
          },
          recipient: {
            id: this.page_id || location.hostname,
          },
        })
      } catch (error) {
        console.error('Failed to clear webchat cookie:', error)
        throw new Error('Failed to clear webchat cookie. Operation aborted.')
      }

      localStorage.clear()

      const currentUrl = new URL(window.location.href)
      currentUrl.searchParams.delete('webchat-token')
      currentUrl.searchParams.delete('token')
      const newUrl = currentUrl.toString()

      const newTab = window.open(newUrl, '_blank')

      if (newTab) {
        setTimeout(() => {
          try {
            window.close()
          } catch (error) {
            console.warn('Cannot close tab automatically due to browser restrictions')
            window.location.href = 'about:blank'
          }
        }, 100)
      }
    },

    /**
     * Fetch live chat status
     */
    fetchLivechatStatus() {
      return axios
        .post(new URL('api/webchat_livechat_check', this.server).href, {
          sender: {
            id: this.uid,
          },
          recipient: {
            id: this.page_id || location.hostname,
          },
        })
        .then((result) => {
          const isTalkingToAgent = _.get(result, 'data.talkingToAgent')
          this.toggleLivechatBanner(isTalkingToAgent)
          const eceActivityId = _.get(result, 'data.eceActivityId')
          this.setEceActivityId(eceActivityId)
        })
    },

    /**
     * Fetch live chat status by row key
     */
    async fetchLivechatStatusByRowkey() {
      const livechatsessionRowKey = localStorage.getItem('livechatsessionRowKey')
      if (_.isEmpty(livechatsessionRowKey)) return false

      const urlWithParams = new URL(
        `api/webchat_livechat_check_by_rowkey?RowKey=${livechatsessionRowKey}`,
        this.server
      ).href
      const result = await axios.get(urlWithParams)

      const isLivechatOngoing = _.get(result, 'data.isUnresolved', false)
      !isLivechatOngoing && localStorage.removeItem('livechatsessionRowKey')

      return isLivechatOngoing
    },

    /**
     * Fetch agent availability
     */
    async fetchAgentAvailability() {
      const result: any = await axios.get(
        new URL('api/webchat_agent_availability_check', this.server).href,
        {
          params: {
            page_url: location.href,
            page_id: location.hostname,
          },
        }
      )
      const agentAvailable = _.get(result, 'data.agentAvailable', false)
      return agentAvailable
    },

    /**
     * Fetch trending topics
     */
    async fetchTrendingTopics() {
      const result: any = await axios.get(new URL('api/webchat_get_trending', this.server).href)
      this.setTrendingTopics(result.data)
    },

    /**
     * Track intent click
     */
    async clickIntent(payload: any) {
      const userIp = this.uid
      const lastActivity = getLastActivityDate()
      const intentId = JSON.stringify(payload)
      return axios.post(new URL('api/webchat_livechat_click_intent', this.server).href, {
        intentId,
        userIp,
        lastActivity,
      })
    },

    /**
     * Fetch configuration from server
     */
    async fetchConfig() {
      const params = new URLSearchParams(location.search)
      const userSource = params.get('user-source') || this.user?.source
      const jwtToken = this.user?.JWT
      const newParams = new URLSearchParams()

      if (userSource) {
        newParams.set('user-source', userSource)
      }
      if (jwtToken) {
        newParams.set('post-login', '1')
      }

      const sourceParam = newParams.toString()
      const configResult: any = await axios.get(
        new URL('api/webchat_config' + (sourceParam ? '?' : '') + sourceParam, this.server).href
      )

      const currentLanguage = localStorage.getItem('currentLanguage') || 'ms'
      localStorage.setItem('currentLanguage', currentLanguage)
      this.setCurrentLanguage(currentLanguage)

      const {
        data: { trendingTopicBanner, trendingTopicTitle, defaultLanguage },
      } = configResult
      if (defaultLanguage) {
        localStorage.setItem('currentLanguage', defaultLanguage)
        this.setCurrentLanguage(defaultLanguage)
      }
      this.setColorTitleTrending(trendingTopicTitle)
      this.setColorBannerTrending(trendingTopicBanner)

      const exclude = _.get(configResult, 'data.exclude', '')
      if (exclude && exclude.indexOf(location.pathname) > -1) {
        return
      } else {
        const displayLauncherOnAgentAvailability = _.get(
          configResult,
          'data.displayLauncherOnAgentAvailability',
          { enabled: false }
        )

        const shouldCheckMaxUsersLimit = _.get(configResult, 'data.shouldCheckMaxUsersLimit', false)

        if (displayLauncherOnAgentAvailability.enabled) {
          const isInUnresolvedLivechat = await this.fetchLivechatStatusByRowkey()

          if (isInUnresolvedLivechat) {
            this.setDisplayLauncher(true)
          } else {
            const DELAY_IN_MS = displayLauncherOnAgentAvailability.delay * 1000
            setTimeout(async () => {
              const agentAvailable = await this.fetchAgentAvailability()
              if (agentAvailable) {
                this.setDisplayLauncher(true)
              } else {
                const checkAgentAvailabilityInterval = setInterval(async () => {
                  const agentAvailable = await this.fetchAgentAvailability()
                  if (agentAvailable) {
                    this.setDisplayLauncher(true)
                    clearInterval(checkAgentAvailabilityInterval)
                  }
                }, 20000)
              }
            }, DELAY_IN_MS)
          }
        } else if (shouldCheckMaxUsersLimit) {
          const webchatUsersNotAtMaxLimit = await this.checkWebchatMaxLimit()
          if (!webchatUsersNotAtMaxLimit) {
            this.setWebchatUsersIsAtMaxLimit(true)
            this.updateShowPreviewBubble({ status: true })
          }
          this.setDisplayLauncher(true)
        } else {
          this.setDisplayLauncher(true)
        }
        return this.reload(configResult.data)
      }
    },

    /**
     * Check webchat max user limit
     */
    async checkWebchatMaxLimit() {
      try {
        const webchatNotAvailable = await axios.get(
          new URL('api/webchat_check_max_limit', this.server).href
        )
        return _.get(webchatNotAvailable, 'data.status', false)
      } catch (error) {
        console.log(error)
        return false
      }
    },

    /**
     * Initialize webchat
     */
    async initialize(initialSettings?: any) {
      if (initialSettings) {
        window.kr_settings = initialSettings
      }

      this.fetchEndpoint()
    },

    /**
     * Fetch JWT token
     */
    async fetchJwt({ user }: { user: any }) {
      const params = getParamsFromUrl()
      const token = params.get('token') || ''

      if (user) {
        if (!user.JWT) {
          if (typeof window.kr_settings.getToken === 'function') {
            const JWT = await window.kr_settings.getToken()
            user.JWT = JWT
          }

          const url = _.get(window, 'kr_settings.getToken.url') as string | undefined
          const method = _.get(window, 'kr_settings.getToken.method') as string | undefined
          const accessTokenPath = (_.get(window, 'kr_settings.getToken.accessTokenPath') || '') as string

          if (isTokenConfigured(url, method)) {
            if (!token) {
              try {
                const JWT = await getNewToken(url!, method! as Method, accessTokenPath)
                user.JWT = JWT
              } catch (err) {
                this.jwtMessage({
                  title: 'Error Get Token',
                  text: 'Error getting token from given url. Please check the getToken.url.',
                })

                return Promise.reject(err)
              }
            }
          }
          if (token) {
            user.JWT = token
          }
        }

        this.user = user

        if (this.user.JWT) {
          const lastJWT = localStorage.getItem('JWT')
          if (lastJWT) {
            const lastJWTPayloadRaw = decodeJwtPayload(lastJWT)
            const currentJWTPayloadRaw = decodeJwtPayload(this.user.JWT)
            const lastJWTPayload = parseJWTPayload(lastJWTPayloadRaw)
            const currentJWTPayload = parseJWTPayload(currentJWTPayloadRaw)

            const currentJWTIsNotEqualToPreviousJWT =
              JSON.stringify(lastJWTPayload) !== JSON.stringify(currentJWTPayload)

            if (currentJWTIsNotEqualToPreviousJWT) {
              this.logout()
            }
          }
          setTokenToAxiosAuthorizationHeader(this.user.JWT)
          localStorage.setItem('JWT', this.user.JWT)
        }
      }
    },

    /**
     * Fetch endpoint configuration
     */
    async fetchEndpoint() {
      if (!window.kr_settings) {
        return
      }
      const { server, bot, user, isPreview } = window.kr_settings

      this.isPreview = isPreview || false

      try {
        await this.fetchJwt({ user })
      } catch (err) {
        return null
      }

      if (server) {
        this.server = server

        if (bot) {
          await new Promise((resolve) => setTimeout(resolve, 1000))
        }

        return this.fetchConfig()
      }
    },

    /**
     * Toggle Zendesk chat
     */
    toggleZendeskChat(toggle: boolean) {
      if (toggle) {
        (window as any).$zopim.livechat.window.show()
      } else {
        (window as any).$zopim.livechat.window.hide()
      }
    },

    /**
     * Reload settings
     */
    reload(settingsReadOnly: any) {
      const settings = Object.assign({}, settingsReadOnly)
      if (window.kr_settings) {
        try {
          if (!_.isEmpty(settings?.apps)) {
            const apps = JSON.parse(decodeURIComponent(window.atob(settings.apps)))
            Object.keys(apps).forEach((key) => {
              if (apps[key] === '') {
                delete apps[key]
              }
            })
            Object.assign(settings, { apps }, window.kr_settings)
          }
          if (settings?.hasTnc === true) {
            settings.showTextbar = localStorage.getItem('showTextbar')
          } else {
            settings.showTextbar = true
          }
          if (settings.cobrowse?.enabled) {
            initCobrowse(window, 'CobrowseIO')
          }
        } catch (error) {
          Sentry.captureException(error)
        }
      } else {
        Object.assign(settings, {
          showTextbar: localStorage.getItem('showTextbar'),
        })
      }

      this.mergeSettings(settings)

      updateThemeVariables(settings)

      if (settings && (settings.openAutomatically === true || settings.forceOpenAutomatically)) {
        const lastOpenStatus = localStorage.getItem('keyreply-lastOpenStatus')
        if (
          (lastOpenStatus == 'true' || settings.forceOpenAutomatically) &&
          !this.webchatUsersIsAtMaxLimit
        ) {
          this.openChatWindowMutation()
          this.start()
        }
      }

      if (settings && settings.sentry) {
        Sentry.init({
          dsn: 'https://5186808e218b4da59e868263ccccff81@sentry.io/242275',
          integrations: [Sentry.browserTracingIntegration()],
          tracesSampleRate: 0.01,
          release: releaseId(),
          environment: process.env.NODE_ENV,
          beforeSend(event) {
            const exception = event.exception
            if (exception) {
              const message = _.get(exception, 'values[0].value', '')

              if (typeof message !== 'string') {
                return null
              }

              const isHttpPermissionError = message.match(/403|401/i)
              const is4XXError = isHttpPermissionError
              const isNetworkError = message.match(
                /network error|timeout of 0ms exceeded|xhr post error|xhr poll error/i
              )

              if (is4XXError || isNetworkError) {
                return null
              }
            }
            return event
          },
        })
      }
    },

    /**
     * Toggle chat window
     */
    toggleChatWindow(isSDK?: boolean) {
      let url = ''
      if (this.chatWindow) {
        this.closeChatWindow()
        url = 'keyreplysdk://?alert=close'
      } else {
        this.openChatWindow()
        url = 'keyreplysdk://?alert=open'
      }

      if (isSDK) {
        window.location.href = url
      }
    },

    /**
     * Open chat window
     */
    openChatWindow() {
      localStorage.setItem('keyreply-lastOpenStatus', 'true')
      this.openChatWindowMutation()
    },

    /**
     * Close chat window
     */
    closeChatWindow() {
      localStorage.setItem('keyreply-lastOpenStatus', 'false')
      this.closeChatWindowMutation()
    },

    /**
     * Toggle Facebook chat window
     */
    toggleFacebookChatWindow() {
      if (this.facebookChatWindow) {
        this.closeFacebookChatWindowMutation()
      } else {
        this.openFacebookChatWindowMutation()
      }
    },

    /**
     * Start live chat session (called from server)
     */
    startLivechat({ eceActivityId, livechatSessionRowKey }: { eceActivityId?: string; livechatSessionRowKey: string }) {
      this.toggleLivechatBanner(true)
      localStorage.setItem('livechatsessionRowKey', livechatSessionRowKey)

      if (eceActivityId) {
        this.setEceActivityId(eceActivityId)
      }
    },

    /**
     * Stop live chat session (called from server)
     */
    stopLivechat() {
      this.toggleLivechatBanner(false)
      this.setCobrowseSession(false)
      localStorage.removeItem('livechatsessionRowKey')
    },

    /**
     * Toggle typing indicator (called from server)
     */
    toggleTypingIndicator(status: { flag: boolean; agentName?: string } | boolean) {
      const statusObj = typeof status === 'boolean' ? { flag: status } : status
      const { flag, agentName } = statusObj

      if (!agentName && !this.liveChatAgentName) {
        this.setLiveChatAgentName('Agent')
      } else if (agentName && agentName !== this.liveChatAgentName) {
        this.setLiveChatAgentName(agentName)
      }

      this.toggleTypingIndicatorMutation(flag)
    },

    /**
     * Ping session to keep alive
     */
    async pingSession() {
      if (this.sessionExpired) {
        await this.login()
      }
      localStorage.setItem('_krlastseen', dayjs().format())
    },

    /**
     * Send a text message
     */
    async sendMessage(payload: { text?: string; id?: string; isPreview?: boolean; lastActivity?: string; [key: string]: any }) {
      const user = this.user
      payload.isPreview = Boolean(this.isPreview)
      payload.lastActivity = getLastActivityDate().toISOString()
      await this.pingSession()

      const id = payload.id || cuid()
      payload.id = id
      if (payload.text) {
        this.addMessage({ text: payload.text, id })
      }
      try {
        const response = await axios.post(new URL('api/webchat', this.server).href, {
          sender: {
            id: this.uid,
            session_id: localStorage.getItem('_krsessionid'),
            appSource: user && user.source ? user.source : null,
            role: user && user.role ? user.role : null,
            signalRConnectionId: this.signalRConnectionId,
          },
          recipient: {
            id: this.page_id || location.hostname,
          },
          message: payload,
          session_length: this.settings.session_length,
          resume_session: this.settings.resume,
        })
        if (response.data[0]) {
          const { options, meta, content } = response.data[0]
          if (meta && meta.lang) {
            if (meta.lang !== 'default') {
              this.setStickyMenuLanguage(meta.lang)
            }
            this.setCurrentLanguage(meta.lang)
          }
          if (
            content[0] &&
            content[0].options &&
            content[0].options.citations &&
            content[0].options.citations.data?.length > 0 &&
            content[0].options.genai
          ) {
            const message: Message = {
              id: cuid(),
              timestamp: new Date(),
              type: 'reply',
              data: response.data[0],
            }
            this.messages.pop()
            this.messages.push(message)
          }
        }
        this.updateMessageStatus({
          responseStatus: _.get(response, 'status'),
          id,
          errorMessage: _.get(response, 'data.message'),
        })

        const jwtExpiredAppFlow = _.get(response, 'data.jwtExpiredAppFlow')
        if (jwtExpiredAppFlow) {
          const requestData = _.get(response, 'data.requestData')
          this.failedRequests.push(requestData)
        }
      } catch (error) {
        this.updateMessageStatusMutation({
          id,
          sent: false,
          errorMessage: FAILED_MESSAGE_ERROR_TEXT,
        })
        throw error
      }
    },

    /**
     * Resend a failed message
     */
    async resendMessage(payload: any) {
      const user = this.user
      const messageId = payload.id
      payload.isPreview = this.isPreview

      await this.pingSession()
      try {
        const response = await axios.post(new URL('api/webchat', this.server).href, {
          sender: {
            id: this.uid,
            session_id: localStorage.getItem('_krsessionid'),
            appSource: user && user.source ? user.source : null,
            role: user && user.role ? user.role : null,
            signalRConnectionId: this.signalRConnectionId,
          },
          recipient: {
            id: this.page_id || location.hostname,
          },
          message: payload,
          session_length: this.settings.session_length,
          resume_session: this.settings.resume,
        })

        this.updateMessageStatus({
          responseStatus: _.get(response, 'status'),
          id: messageId,
          errorMessage: _.get(response, 'data.message'),
        })

        const jwtExpiredAppFlow = _.get(response, 'data.jwtExpiredAppFlow')
        if (jwtExpiredAppFlow) {
          const requestData = _.get(response, 'data.requestData')
          this.failedRequests.push(requestData)
        }
      } catch (error) {
        this.updateMessageStatusMutation({
          id: messageId,
          sent: false,
          errorMessage: FAILED_MESSAGE_ERROR_TEXT,
        })
        throw error
      }
    },

    /**
     * Send a postback (button click)
     */
    async sendPostback({ event, data, text, options = {}, trendingText }: {
      event: string
      data: any
      text?: string
      options?: any
      trendingText?: string
    }) {
      try {
        if (!this.isSendingPostback) {
          this.setSendingPostback(true)
          await this.pingSession()

          const user = this.user
          const isPreview = this.isPreview

          const id = cuid()
          const feedbackCollection = data?.options?.feedbackCollection || false
          text = text ? text : trendingText
          if (text && !feedbackCollection) {
            this.addMessage({ type: 'postback', text, id, trendingText })
          }

          const response = await axios.post(new URL('api/webchat', this.server).href, {
            id,
            sender: {
              id: this.uid,
              session_id: localStorage.getItem('_krsessionid'),
              appSource: user && user.source ? user.source : null,
              role: user && user.role ? user.role : null,
              signalRConnectionId: this.signalRConnectionId,
            },
            recipient: {
              id: this.page_id || location.hostname,
            },
            postback: {
              event,
              data,
              text,
              isPreview,
              options,
            },
            session_length: this.settings.session_length,
            resume_session: this.settings.resume,
          })
          this.setSendingPostback(false)
          const jwtExpiredAppFlow = _.get(response, 'data.jwtExpiredAppFlow')
          if (jwtExpiredAppFlow) {
            const requestData = _.get(response, 'data.requestData')
            this.failedRequests.push(requestData)
          }
        }
      } catch (error) {
        this.setSendingPostback(false)
      }
    },

    /**
     * Close webview
     */
    closeWebview() {
      this.setWebviewUrl('')
      this.setShowWebview(false)
    },

    /**
     * Build a proxy URL that streams file content via the server
     */
    toFileProxyUrl(blobNameOrUrl: string): string {
      const isFullUrl = /^https?:\/\//i.test(blobNameOrUrl)
      const blobName = isFullUrl
        ? new URL(blobNameOrUrl).pathname
        : blobNameOrUrl
      const proxyUrl = new URL('api/webchat_request_file', this.server)
      proxyUrl.searchParams.set('blobName', blobName)
      proxyUrl.searchParams.set('requesterId', this.uid)
      proxyUrl.searchParams.set('page_id', this.page_id || location.hostname)
      const jwt = this.user?.JWT || localStorage.getItem('JWT') || ''
      if (jwt) {
        proxyUrl.searchParams.set('token', jwt)
      }
      return proxyUrl.href
    },

    /**
     * Upload files
     */
    async uploadFile({ files, vm }: { files: FileList | File[]; vm: any }) {
      try {
        const url = new URL('api/webchat_file_upload', this.server)
        url.searchParams.set('requesterId', this.uid)
        url.searchParams.set('page_id', this.page_id || location.hostname)
        const formData = new FormData()

        _.each(files, (file: File) => {
          formData.append('file', file, file.name)
        })

        const { data: blobNames } = await axios.post(url.href, formData)

        if (blobNames?.length) {
          const proxyUrls = blobNames.map((name: string) => this.toFileProxyUrl(name))
          await this.sendFile(proxyUrls)
        }

        await this.uploadFileToThirdParties(formData)
      } catch (error) {
        throw error
      }
    },

    /**
     * Upload file to third parties
     */
    async uploadFileToThirdParties(data: FormData) {
      const FILEUPLOAD_URL = new URL(
        `api/webchat_file_upload_to_third_parties?requesterId=${this.uid}&page_id=${
          this.page_id || location.hostname
        }`,
        this.server
      ).href
      await axios.post(FILEUPLOAD_URL, data)
    },

    /**
     * Send file URLs in chat
     */
    async sendFile(filesUrls: string[]) {
      const imageRegex = /\.(gif|jpe?g|tiff?|png|webp|bmp)/i
      const audioRegex = /\.(wav)/i
      const endpoint = new URL('api/webchat', this.server).href

      filesUrls = _.compact(filesUrls)

      const payloads = _.reduce(
        filesUrls,
        function ({ imageUrls, audioUrls, nonImageUrls }: { imageUrls: string[]; audioUrls: string[]; nonImageUrls: string[] }, url: string) {
          if (imageRegex.test(url)) {
            imageUrls.push(url)
          } else if (audioRegex.test(url)) {
            audioUrls.push(url)
          } else {
            nonImageUrls.push(url)
          }

          return { imageUrls, audioUrls, nonImageUrls }
        },
        {
          imageUrls: [] as string[],
          audioUrls: [] as string[],
          nonImageUrls: [] as string[],
        }
      )

      const promises = _.map(payloads, (urls: string[], key: string) => {
        if (_.isEmpty(urls)) {
          return
        }

        const payload: any = {
          isPreview: this.isPreview,
        }

        if (key === 'imageUrls') {
          payload.images = urls
        } else if (key === 'audioUrls') {
          payload.audio = urls[0]
        } else {
          payload.files = urls
        }

        const id = cuid()

        this.sendFileMessage({ content: payload, id })
        try {
          const response = axios.post(endpoint, {
            id,
            sender: {
              id: this.uid,
              appSource: this.user?.source,
              role: this.user?.role,
              session_id: localStorage.getItem('_krsessionid'),
            },
            recipient: { id: this.page_id || location.hostname },
            message: payload,
            session_length: this.settings.session_length,
            resume_session: this.settings.resume,
          })

          if (key !== 'audioUrls') {
            this.updateMessageStatus({
              responseStatus: _.get(response, 'status'),
              id,
              errorMessage: _.get(response, 'data.message'),
            })
          }

          return response
        } catch (error) {
          this.updateMessageStatusMutation({
            id,
            sent: false,
            errorMessage: FAILED_MESSAGE_ERROR_TEXT,
          })
          throw error
        }
      })

      return Promise.all(_.compact(promises))
    },

    /**
     * Resend failed file upload
     */
    async resendFile({ filesUrls, id, timestamp }: { filesUrls: string[]; id: string; timestamp: Date }) {
      const imageRegex = /\.(gif|jpe?g|tiff?|png|webp|bmp)/i
      const endpoint = new URL('api/webchat', this.server).href

      filesUrls = _.compact(filesUrls)

      const payloads = _.reduce(
        filesUrls,
        function ({ imageUrls, nonImageUrls }: { imageUrls: string[]; nonImageUrls: string[] }, url: string) {
          if (imageRegex.test(url)) {
            imageUrls.push(url)
          } else {
            nonImageUrls.push(url)
          }

          return { imageUrls, nonImageUrls }
        },
        {
          imageUrls: [] as string[],
          nonImageUrls: [] as string[],
        }
      )

      const promises = _.map(payloads, (urls: string[], key: string) => {
        if (_.isEmpty(urls)) {
          return
        }

        const payload: any = {
          isPreview: this.isPreview,
        }

        if (key === 'imageUrls') {
          payload.images = urls
        } else {
          payload.files = urls
        }

        payload.timestamp = timestamp

        try {
          const response = axios.post(endpoint, {
            id,
            sender: {
              id: this.uid,
              appSource: this.user?.source,
              role: this.user?.role,
              session_id: localStorage.getItem('_krsessionid'),
            },
            recipient: { id: this.page_id || location.hostname },
            message: payload,
            session_length: this.settings.session_length,
            resume_session: this.settings.resume,
          })

          this.updateMessageStatus({
            responseStatus: _.get(response, 'status'),
            id,
            errorMessage: _.get(response, 'data.message'),
          })
          return response
        } catch (error) {
          this.updateMessageStatusMutation({
            id,
            sent: false,
            errorMessage: FAILED_MESSAGE_ERROR_TEXT,
          })
          throw error
        }
      })

      return Promise.all(_.compact(promises))
    },

    /**
     * Update message status based on response
     */
    updateMessageStatus({ responseStatus, id, errorMessage }: { responseStatus?: number; id: string; errorMessage?: string }) {
      if (responseStatus === 200) {
        this.updateMessageStatusMutation({ id, sent: true })
      } else {
        this.updateMessageStatusMutation({
          id,
          sent: false,
          errorMessage: errorMessage || FAILED_MESSAGE_ERROR_TEXT,
        })
      }
    },

    /**
     * Close dialog
     */
    closeDialog() {
      this.closeDialogMutation()
    },

    /**
     * Email chat history
     */
    emailHistory(payload: any) {
      const endpoint = new URL('api/webchat_email_history', this.server).href

      return axios.post(endpoint, {
        user_id: this.uid,
        page_id: this.page_id || location.hostname,
        token: this.token,
        payload,
      })
    },

    /**
     * Build a proxy URL for an existing file reference (no HTTP request needed)
     */
    async fetchFileUrl(fileUrl: string | { url: string }) {
      const url = _.get(fileUrl, 'url', fileUrl) as string

      if (url.includes('webchat_request_file')) {
        const urlObj = new URL(url)
        urlObj.searchParams.set('requesterId', this.uid)
        urlObj.searchParams.set('page_id', this.page_id || location.hostname)
        const jwt = this.user?.JWT || localStorage.getItem('JWT') || ''
        if (jwt) {
          urlObj.searchParams.set('token', jwt)
        }
        return { data: urlObj.href }
      }

      return { data: this.toFileProxyUrl(url) }
    },

    /**
     * Update agent typing indicator
     */
    async updateAgentTypingIndicator(status: boolean) {
      return axios.post(new URL('api/webchat_livechat_update_typing_indicator', this.server).href, {
        partition_key: this.page_id || location.hostname,
        user_id: this.uid,
        status,
      })
    },

    /**
     * Remote clear - reset everything
     */
    async remoteClear() {
      await this.logout()
      await this.stopLivechat()
      await this.register()
      await this.login()
      await this.listen()
      await this.sendStartMessage()
    },

    /**
     * Get live chat status
     */
    getLivechatStatus() {
      return _.get(this, 'settings.liveChatStatus', false)
    },

    /**
     * Send pre-chat form details
     */
    async sendPrechatformDetails(form: any) {
      const result = await axios.post(new URL('api/prechatform_details', this.server).href, {
        sender: {
          id: this.uid,
          session_id: localStorage.getItem('_krsessionid'),
        },
        recipient: {
          id: this.page_id || location.hostname,
        },
        form,
      })
      const stateSet = _.get(result, 'data', false)
      return stateSet
    },

    /**
     * Get pre-chat form settings
     */
    async getPrechatformSettings() {
      const result = await axios.get(new URL('api/prechatform_settings', this.server).href)

      const prechatFormSettings = _.get(result, 'data.prechatFormSettings', {})
      return prechatFormSettings
    },

    /**
     * Send post-chat form details
     */
    async sendPostchatformDetails(form: any) {
      const result = await axios.post(new URL('api/postchatform_details', this.server).href, {
        sender: {
          id: this.uid,
          session_id: localStorage.getItem('_krsessionid'),
        },
        recipient: {
          id: this.page_id || location.hostname,
        },
        form,
      })
      const stateSet = _.get(result, 'data', false)
      return stateSet
    },

    /**
     * Get post-chat form settings
     */
    async getPostchatformSettings() {
      const result = await axios.get(new URL('api/postchatform_settings', this.server).href)

      const postchatFormSettings = _.get(result, 'data.postchatFormSettings', {})
      return postchatFormSettings
    },

    /**
     * Fetch cobrowse session status
     */
    fetchCobrowseStatus() {
      return axios
        .post(new URL('api/webchat_cobrowse_check', this.server).href, {
          sender: {
            id: this.uid,
          },
          recipient: {
            id: this.page_id || location.hostname,
          },
        })
        .then((result) => {
          const inCobrowseSession = _.get(result, 'data.inCobrowseSession')
          if (inCobrowseSession) {
            this.startCobrowse()
          }
        })
    },

    /**
     * Start cobrowse session
     */
    startCobrowse() {
      this.setCobrowseSession(true)
    },

    /**
     * Stop cobrowse session
     */
    stopCobrowse() {
      if (!this.promptedCobrowseEnded) {
        ElMessageBox.alert('Cobrowsing session has ended', 'Support Ended', {
          confirmButtonText: 'Close',
        })
        this.promptedCobrowseEnded = true
        this.setCobrowseSession(false)
        setTimeout(() => {
          this.promptedCobrowseEnded = false
        })
      }
    },

    /**
     * Get Scribe token
     */
    async getScribeToken() {
      try {
        const response = await axios.get(new URL('api/scribe_token', this.server).href)
        return response.data
      } catch (error) {
        console.log(error)
        return false
      }
    },

    /**
     * Get citation details for GenAI responses
     */
    async getCitationDetails(payload: any) {
      try {
        const response = await axios.post(
          new URL('api/genai_get_document_details', this.server).href,
          payload
        )
        return response.data
      } catch (error) {
        console.log(error)
        return false
      }
    },
  },
})
